import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Lock, CheckCircle2 } from "lucide-react";

/**
 * Two-step Google SSO — Step 2: shown when /api/auth/google replies
 * with `pending_signup: true` and a `pending_signup_token`.
 *
 * Renders the Google profile preview + a role catalogue (all platform
 * roles except super_admin) and posts the choice to
 * /api/auth/google/complete-signup via the supplied `onComplete`.
 */
export default function GoogleRolePicker({ pendingToken, profile, onComplete, onCancel }) {
  const [roles, setRoles] = useState([]);
  const [selected, setSelected] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loadingRoles, setLoadingRoles] = useState(true);

  useEffect(() => {
    let alive = true;
    api
      .get("/auth/google/signup-roles")
      .then(({ data }) => {
        if (!alive) return;
        setRoles(data.roles || []);
      })
      .catch(() => {
        if (!alive) return;
        toast.error("Could not load roles — please retry.");
      })
      .finally(() => alive && setLoadingRoles(false));
    return () => {
      alive = false;
    };
  }, []);

  const handleSubmit = async () => {
    if (!selected) {
      toast.error("Please choose your primary role to continue.");
      return;
    }
    setSubmitting(true);
    const res = await onComplete(pendingToken, selected);
    setSubmitting(false);
    if (!res.ok) toast.error(res.error || "Could not complete sign-up.");
  };

  return (
    <div data-testid="google-role-picker" className="w-full">
      <div className="rounded-xl border border-slate-200 bg-white p-5 mb-6 flex items-center gap-4" data-testid="google-profile-card">
        {profile?.picture ? (
          <img
            src={profile.picture}
            alt={profile.name}
            className="h-14 w-14 rounded-full ring-2 ring-[#D4AF37]/40"
            referrerPolicy="no-referrer"
            data-testid="google-profile-avatar"
          />
        ) : (
          <div className="h-14 w-14 rounded-full bg-[#0F4C81] text-white flex items-center justify-center font-bold text-lg">
            {(profile?.name || profile?.email || "?")[0]?.toUpperCase()}
          </div>
        )}
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-emerald-700">
              Verified by Google
            </p>
          </div>
          <p className="mt-1 text-base font-semibold text-[#0A192F] truncate" data-testid="google-profile-name">
            {profile?.name}
          </p>
          <p className="text-xs text-slate-500 truncate" data-testid="google-profile-email">
            {profile?.email}
          </p>
        </div>
      </div>

      <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
        One last step
      </p>
      <h2 className="mt-2 text-2xl sm:text-3xl font-bold tracking-tighter text-[#0A192F]">
        Choose your role on Diplomexa.
      </h2>
      <p className="mt-2 text-sm text-slate-500">
        We tailor your dashboard, tools, and certificates around the role
        you pick. You can request a change later from your profile.
      </p>

      <div
        className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-h-[280px] overflow-y-auto pr-1"
        data-testid="google-role-options"
      >
        {loadingRoles ? (
          <p className="text-sm text-slate-400 col-span-2">Loading roles…</p>
        ) : (
          roles.map((r) => {
            const active = selected === r.key;
            return (
              <button
                key={r.key}
                type="button"
                onClick={() => setSelected(r.key)}
                data-testid={`role-option-${r.key}`}
                aria-pressed={active}
                className={
                  "text-left px-4 py-3 rounded-lg border transition-colors " +
                  (active
                    ? "border-[#D4AF37] bg-[#FFFBEC] ring-1 ring-[#D4AF37]"
                    : "border-slate-200 hover:border-[#0F4C81] bg-white")
                }
              >
                <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">
                  {r.key.replace(/_/g, " ")}
                </p>
                <p className="mt-1 text-sm font-semibold text-[#0A192F]">
                  {r.label}
                </p>
              </button>
            );
          })
        )}
      </div>

      <Button
        type="button"
        onClick={handleSubmit}
        disabled={submitting || !selected}
        className="w-full mt-6 h-12 bg-[#0F4C81] hover:bg-[#0A3860] text-white font-semibold uppercase tracking-[0.16em] text-sm disabled:opacity-60"
        data-testid="google-complete-signup-button"
      >
        {submitting ? "Creating your workspace…" : "Continue"}
      </Button>

      <button
        type="button"
        onClick={onCancel}
        className="w-full mt-3 text-xs text-slate-500 hover:text-[#0F4C81] underline"
        data-testid="google-cancel-signup"
      >
        Use a different Google account
      </button>

      <p className="text-[11px] text-slate-400 text-center mt-4 flex items-center justify-center gap-1.5">
        <Lock className="h-3 w-3" /> Secured login powered by Google
      </p>
    </div>
  );
}
